import { useState } from 'react';
import { Card, CARD_SECTION_TITLE_CLASS, TextButton } from '@safetyhub/ui';

/**
 * 「전체 실행」 자리에 들어가는 **안내** — 버튼이 아니다.
 *
 * ## 🔴 왜 실행 버튼이 아닌가
 * **서버는 불변식을 돌리지 않고 저장만 한다**(사용자 결정 2026-10-12). 컨트롤러가 그 선을 docblock에
 * 적어 두었다 — 「그래서 「실행」 엔드포인트가 없다. 그 선을 서버로 옮기려면 러너가 DB에 붙어야
 * 하고, 그것이 **prod 자격증명을 로컬에 두지 않는다**는 규율과 충돌한다 — 조용히 추가하지 말 것」.
 * 근거 둘: ⓐ 러너가 DB에 접속하지 않는 것이 판정을 **순수 함수**로 유지한다(합성 fixture로
 * `vacuous`를 실제로 낼 수 있다) ⓑ 불변식 선언이 `scripts/stories/invariants/*.mjs`에 있어 **서버
 * 번들 밖**이라, 서버가 돌리려면 선언을 옮겨야 하고 **두 벌이 되면 drift**다.
 *
 * ⇒ 눌리는 버튼을 두면 화면이 **거짓 안내**를 한다. 대신 사람이 치는 순서를 그대로 보여준다.
 * 명령 문구의 정본은 `scripts/stories/invariants/run.mjs` 헤더이고, 바뀌면 여기도 고칠 것.
 */

interface Step {
  label: string;
  command: string;
  note: string;
}

const STEPS: Step[] = [
  {
    label: '① SQL 뽑기',
    command: 'pnpm story:invariants --emit --combined',
    note: '전 불변식을 «한 SQL»로 낸다 — MCP 한 번으로 전부 잰다. 불변식마다 따로 치면 결과 숫자를 손으로 옮기게 되고 그 전사가 가장 큰 오류원이다.',
  },
  {
    label: '② 위반 건 식별자 SQL (선택)',
    command: 'pnpm story:invariants --emit --identify',
    note: '위반 «건»의 식별자를 뽑는 별 섹션. 이게 없으면 판정은 되지만 story_violations를 채울 수 없다(집계만 남는다).',
  },
  {
    label: '③ DB에서 실행',
    command: '-- MCP: mcp__safetyhub-monitor-prod__server_db_query (prod) / …monitor__… (dev)',
    note: '세로형 표 `inv | col | value`가 그대로 ④의 입력이다. ⚠️ MCP=prod 가정 금지 — hostname·docker ps로 대상을 먼저 확인할 것.',
  },
  {
    label: '④ 판정',
    command: 'pnpm story:invariants --judge <결과파일> --env prod --measured-at <ISO>',
    note: '결과를 먹어 pass/violation/vacuous/error를 낸다. ②를 뽑았으면 `--identified <파일>`을 함께. ⚠️ `--measured-at`은 SQL을 «실제로 돌린» 시각이다(기록 시각과 다르다).',
  },
  {
    label: '⑤ 저장',
    command: 'POST /api/v1/admin/stories/check-runs  (④의 산출물 한 벌)',
    note: '쓰기는 반드시 API다. tally는 서버가 results에서 계산하고 클라이언트가 보낸 수는 받지 않는다. 미등록 storyId·(스토리, 불변식) 중복은 400이다.',
  },
];

function CopyableCommand({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    // 클립보드는 보안 컨텍스트가 아니거나 권한이 없으면 던진다 — 실패해도 화면은 멀쩡해야 한다
    navigator.clipboard?.writeText(command).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => setCopied(false),
    );
  };

  return (
    <div className="flex items-start gap-2">
      <code className="flex-1 break-all rounded-card-sm bg-ink-900 px-3 py-2 text-[12.5px] font-semibold text-white">
        {command}
      </code>
      <TextButton onClick={copy}>{copied ? '복사됨' : '복사'}</TextButton>
    </div>
  );
}

export default function RunnerGuide() {
  return (
    <Card>
      <h2 className={CARD_SECTION_TITLE_CLASS}>불변식 판정 실행 방법</h2>
      <p className="mt-2 text-xs leading-relaxed text-ink-600">
        <strong className="text-ink-800">이 화면에 「전체 실행」 버튼이 없는 것은 결함이 아니다</strong> —
        서버는 불변식을 <strong>돌리지 않고 저장만</strong> 한다(사용자 결정 2026-10-12). 러너가 판정을
        소유하는 이유는 ⓐ DB에 붙지 않는 것이 판정을 <strong>순수 함수</strong>로 유지하고(합성
        fixture로 「공허」를 실제로 낼 수 있다) ⓑ 불변식 선언이 서버 번들 밖에 있어 서버가 돌리려면{' '}
        <strong>선언이 두 벌</strong>이 되기 때문이다. 눌리는 버튼을 두면 화면이 거짓 안내를 한다.
      </p>

      <ol className="mt-4 flex flex-col gap-4">
        {STEPS.map((step) => (
          <li key={step.label} className="flex flex-col gap-1.5">
            <span className="text-[13px] font-bold text-ink-800">{step.label}</span>
            <CopyableCommand command={step.command} />
            <span className="text-[11.5px] leading-relaxed text-ink-500">{step.note}</span>
          </li>
        ))}
      </ol>

      <p className="mt-4 border-t border-line pt-3 text-[11.5px] leading-relaxed text-ink-500">
        명령 문구의 정본은 <code>scripts/stories/invariants/run.mjs</code> 헤더다. 숙성 분포와
        커버리지 두 비율은 <code>pnpm story:check</code>가 낸다(이 화면은 그 수를 받지 못한다 —
        위 「보장 수단」 카드 참조).
      </p>
    </Card>
  );
}
